// 표준화된 RADAR 차트 옵션 생성
import { formatValue } from '../../../utils/numberFormatter';
import { applyCommonStyles } from './applyCommonStyles';

export const generateRadarChartOption = (data, baseOption, options = {}) => {
  
  
  // 표준 구조: { categories: [...], series: [{ name: "...", values: [...] }] }
  const categories = data.categories || [];
  let series = data.series || [];
  
  // Backend compatibility: convert 'data' to 'values' if needed
  series = series.map(s => {
    if (s && s.data && !s.values) {
      
      return { ...s, values: s.data };
    }
    return s;
  });
  
  
  
  // 데이터 형식 검증
  if (!Array.isArray(categories) || !Array.isArray(series)) {
    
    return {
      ...baseOption,
      title: { ...baseOption.title, text: '레이더 차트 데이터 오류' },
      series: [],
    };
  }

  // 지표(indicator) 설정 - 정규화된 값에 맞게 max 값을 100으로 설정
  const radarIndicators = categories.map(category => {
    if (typeof category === 'string') {
      return { name: category, max: 100 }; // 정규화된 값은 0-100 범위
    }
    if (typeof category === 'object' && category !== null && category.name) {
      return { name: category.name, max: category.max || 100 };
    }
    
    return null;
  }).filter(indicator => indicator !== null);

  if (radarIndicators.length === 0) {
    
    return { ...baseOption, title: { ...baseOption.title, text: '레이더 지표 없음' }, series: [] };
  }

  // 수동 정규화 옵션 확인
  const useManualNormalization = options.advanced?.manualNormalization === true;
  const manualMin = Number(options.advanced?.normalizationMin) || 0;
  const manualMax = Number(options.advanced?.normalizationMax) || 100;

  // 데이터 정규화 처리
  let globalMinValue = 0;
  let globalMaxValue = 100;

  if (useManualNormalization) {
    // 수동 정규화: 사용자가 지정한 min/max 값 사용
    globalMinValue = manualMin;
    globalMaxValue = manualMax;
  } else {
    // 자동 정규화: 0을 기준으로 최대값만 계산 (레이더 차트 표준 방식)
    const allValues = [];
    series.forEach(s => {
      if (s && Array.isArray(s.values)) {
        s.values.forEach(v => {
          const numValue = Math.abs(Number(v) || 0); // 절댓값으로 처리
          allValues.push(numValue);
        });
      }
    });

    if (allValues.length > 0) {
      globalMinValue = 0; // 레이더 차트는 항상 0부터 시작
      globalMaxValue = Math.max(...allValues);
      // 모든 값이 0인 경우 처리
      if (globalMaxValue === 0) {
        globalMaxValue = 1;
      }
    }
  }

  const normalizedSeries = series.map(s => {
    if (!s || typeof s.name !== 'string' || !Array.isArray(s.values)) {


      return null;
    }

    // 정규화된 값 계산 (수동/자동 모두 적용)
    const normalizedValues = normalizeRadarValues(s.values, globalMinValue, globalMaxValue);

    return {
      name: s.name,
      value: normalizedValues,
      originalValues: s.values, // 원본 값 저장
      itemStyle: s.itemStyle,
      lineStyle: s.lineStyle,
      areaStyle: s.areaStyle,
      label: s.label
    };
  }).filter(s => s !== null);


  if (normalizedSeries.length === 0) {
    
    return { ...baseOption, title: { ...baseOption.title, text: '레이더 시리즈 데이터 없음' }, series: [] };
  }

  const option = {
    ...baseOption,
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params) => {
        let tooltipHtml = params.name ? `${params.name}<br/>` : '';
        if (params.value && Array.isArray(params.value)) {
          // 해당 시리즈의 원본 값 찾기
          const currentSeries = normalizedSeries.find(s => s.name === params.name);
          const originalValues = currentSeries ? currentSeries.originalValues : null;
          
          params.value.forEach((val, index) => {
            const indicator = radarIndicators[index];
            if (indicator) {
              const originalValue = originalValues ? originalValues[index] : null;
              const displayValue = originalValue !== null ? formatValue(originalValue) : formatValue(val);
              tooltipHtml += `${indicator.name}: ${displayValue}<br/>`;
            }
          });
        }
        return tooltipHtml;
      }
    },
    legend: {
      data: normalizedSeries.map(s => s.name),
      bottom: options.advanced?.legendBottom || '8%',
      orient: options.advanced?.legendOrient || 'horizontal',
      textStyle: { fontSize: 10 },
      icon: 'rect',
      itemWidth: 10,
      itemHeight: 10,
    },
    radar: {
      indicator: radarIndicators,
      center: options.advanced?.radarCenter || ['50%', '52%'],
      radius: options.advanced?.radarRadius || '60%',
      startAngle: options.advanced?.radarStartAngle || 90,
      splitNumber: options.advanced?.radarSplitNumber || 5,
      axisName: {
        formatter: '{value}',
        color: '#333',
        fontSize: 10,
      },
      splitArea: {
        areaStyle: {
          color: ['rgba(250,250,250,0.3)', 'rgba(200,200,200,0.3)'],
          shadowBlur: 10
        }
      },
      axisLine: {
        lineStyle: {
          color: 'rgba(150,150,150,0.5)'
        }
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(150,150,150,0.5)'
        }
      }
    },
    series: [
      {
        name: baseOption.title?.text || 'Radar Chart',
        type: 'radar',
        data: normalizedSeries,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: {
          width: 2
        },
        areaStyle: {
          opacity: 0.1
        },
        label: {
          show: options.advanced?.showDataLabels === true ? true : false,
          position: options.advanced?.labelPosition || 'top',
          formatter: (params) => {
            // 레이더는 각 축의 값을 한 줄로 보여주기보다는 시리즈 이름과 평균값 등을 표시
            // 기본은 시리즈 이름과 각 지표의 원본 값을 쉼표로 나열
            if (params.value && Array.isArray(params.value)) {
              const currentSeries = normalizedSeries.find(s => s.name === params.name);
              const originals = currentSeries?.originalValues || [];
              const joined = originals.map(v => formatValue(v)).join(', ');
              const fmt = (options.advanced?.labelFormat || '{seriesName}: {value}')
                .replace('{seriesName}', params.name ?? '')
                .replace('{value}', joined);
              return fmt;
            }
            return params.name || '';
          }
        }
      }
    ]
  };

  // 레이더 차트는 x/y 축, 그리드가 필요 없음
  delete option.grid;
  delete option.xAxis;
  delete option.yAxis;

  // 제목 위치 조정
  option.title.left = 'center';
  option.title.top = '5%';
  // 제목 스타일 개선
  option.title.textStyle = {
    fontSize: 20,
    fontWeight: '600',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    color: '#2c3e50',
    lineHeight: 1.2,
    padding: [0, 0, 8, 0]
  };

  // 공통 스타일 적용
  let finalOption = options.style ? applyCommonStyles(option, options.style) : option;

  // transparent 처리 (ECharts 차트 배경 투명도)
  // showFrame은 DashboardComponentRenderer에서 처리하므로 여기서는 transparent만 처리
  if ((options.transparent === true || options.transparent === 'true')) {
    // ECharts 전체 배경을 투명하게 설정
    finalOption.backgroundColor = 'transparent';
  }

  return finalOption;
};

/**
 * 레이더 차트 값들을 0-100 범위로 정규화
 * min/max 값을 기준으로 정규화
 */
function normalizeRadarValues(values, minValue, maxValue) {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }

  const range = maxValue - minValue || 1; // 0으로 나누기 방지

  // min-max 범위를 0-100으로 정규화
  const normalizedValues = values.map(v => {
    const numValue = Number(v) || 0;

    // 자동 정규화에서 minValue가 0인 경우 (표준 레이더 차트)
    if (minValue === 0 && maxValue > 0) {
      // 간단한 비율 계산: value / max * 100
      const normalized = (numValue / maxValue) * 100;
      return Math.max(0, Math.min(normalized, 100));
    }

    // 수동 정규화 또는 특수한 경우
    // (value - min) / (max - min) * 100
    const normalized = ((numValue - minValue) / range) * 100;
    // 0-100 범위로 제한
    return Math.max(0, Math.min(normalized, 100));
  });

  return normalizedValues;
} 